import React, { useEffect, useRef } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { Helmet } from "react-helmet";

// Hooks
import { useLocation, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector, usePrevious } from "../../hooks";

// Actions
import { searchArticles } from "../../store/actions";

// Components
import Loader from "../../components/Loader";
import Container from "../../components/Container";
import ArticleCard from "../../components/ArticleCard";
import Input from "../../components/SearchInput";
import Title from "../../components/Typography/Title";

// Pages
import ErrorPage from "../Error";

// Types
import { Article } from "../../store/actions/types";

// Style
import * as S from "../../components/Grid/style";
import ArticleNotFound from "../ArticleNotFound";
import { RAZZLE_APP_PORTAL_LINK } from "../../constants";

interface Query {
  queryText: string;
}

const Search: React.FC = () => {
  const location = useLocation();
  const { queryText } = useParams<Query>();
  const dispatch = useAppDispatch();
  const prevQueryText = usePrevious(queryText);
  const { promiseInProgress } = usePromiseTracker();
  const { error } = useAppSelector((state) => state.articles);
  const articles = useAppSelector((state) => state.articles.data.articles);
  const isFirstMount = useRef(true);
  const onPageReload = () => {
    if (!queryText) {
      return;
    }

    if (isFirstMount.current) {
      trackPromise(dispatch(searchArticles(queryText)));

      isFirstMount.current = false;
    }
  };

  const onPageChange = () => {
    if (!isFirstMount.current && prevQueryText) {
      dispatch(searchArticles(queryText));
    }
  };

  useEffect(onPageReload, [dispatch, isFirstMount, queryText]);
  useEffect(onPageChange, [dispatch, isFirstMount, queryText]);

  const renderArticles = articles.map(
    ({
      id,
      title,
      created,
      subjects,
      text,
      parent,
      creators,
      effective,
    }: Article) => (
      <ArticleCard
        isSecondary
        key={id}
        id={id}
        title={title}
        created={created}
        subjects={subjects}
        creators={creators}
        text={text}
        parent={parent}
        effective={effective}
      />
    )
  );

  return (
    <>
      <Helmet>
        <title>Unitiva - Magazine</title>
        <meta
          name="description"
          content="Consulenza Plone, Plone Consulting, Open Source, App Mobile, Web App, eCommerce, Portali web"
        />
        <meta property="og:title" content="Unitiva - Magazine" />
        <meta
          property="og:description"
          content="Consulenza Plone, Plone Consulting, Open Source, App Mobile, Web App, eCommerce, Portali web"
        />
        <meta
          property="og:image"
          content="https://www.unitiva.it/img/logo.svg"
        />
        <meta property="og:image:type" content="image/png" />
        <meta
          property="og:url"
          content={
            typeof window !== "undefined"
              ? window.location.href
              : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
          }
        />
        <link
          rel="canonical"
          href={
            typeof window !== "undefined"
              ? window.location.href
              : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
          }
        />
      </Helmet>
      {error.message ? (
        <ErrorPage message={error.message} />
      ) : (
        <>
          <Container>
            {promiseInProgress ? (
              <Loader />
            ) : (
              <>
                {articles.length === 0 ||
                typeof prevQueryText === "undefined" ? (
                  <>
                    <Input />
                    <ArticleNotFound
                      title={`${
                        !queryText
                          ? "No articles found"
                          : `No articles found for ${queryText}`
                      }`}
                    />
                  </>
                ) : (
                  <>
                    <Input />
                    <S.Articles>
                      <Title title={`Risultati per ${queryText}`} />
                      <S.SecondaryArticlesGrid>
                        {renderArticles}
                      </S.SecondaryArticlesGrid>
                    </S.Articles>
                  </>
                )}
              </>
            )}
          </Container>
        </>
      )}
    </>
  );
};

export default Search;
